import { useEffect, type ReactNode } from 'react'
import { IconX } from '@tabler/icons-react'

type Props = {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
  headerActions?: ReactNode
  narrow?: boolean   // ~540px centered for small forms
  wide?: boolean     // ~1080px centered for larger forms like gear items
}

export function FullScreenModal({ open, onClose, title, subtitle, children, footer, headerActions, narrow, wide }: Props) {
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => { document.body.style.overflow = '' }
  }, [open])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    if (open) document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  if (narrow) {
    return (
      <div className="fixed inset-0 z-[200] flex items-start justify-center bg-black/70 backdrop-blur-sm pt-12 pb-8 px-4 animate-fade-in">
        <div className="w-full max-w-[540px] bg-surface-overlay border border-border-strong rounded-2xl shadow-2xl flex flex-col max-h-[calc(100vh-80px)]">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-border-default shrink-0">
            <button onClick={onClose}
              className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-100 hover:bg-surface-raised transition-colors">
              <IconX size={16} />
            </button>
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-semibold text-zinc-100 truncate">{title}</h2>
              {subtitle && <p className="text-xs text-zinc-400 truncate">{subtitle}</p>}
            </div>
            {headerActions && <div className="flex items-center gap-2 shrink-0">{headerActions}</div>}
            {footer && !headerActions && <div className="flex items-center gap-2 shrink-0">{footer}</div>}
          </div>
          <div className="flex-1 overflow-y-auto">{children}</div>
          {footer && (
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border-default shrink-0">
              {footer}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (wide) {
    return (
      <div className="fixed inset-0 z-[200] flex flex-col sm:items-center sm:justify-center bg-surface-base sm:bg-black/75 sm:backdrop-blur-sm sm:pt-8 sm:pb-8 sm:px-6 animate-fade-in">
        <div className="w-full flex flex-col h-full sm:h-auto sm:max-h-[calc(100vh-64px)] sm:max-w-[1080px] bg-surface-overlay sm:border sm:border-border-strong sm:rounded-2xl shadow-2xl">
          {/* Header */}
          <div className="flex items-center gap-3 sm:gap-4 px-4 py-3 sm:px-6 sm:py-4 border-b border-border-default shrink-0">
            <button onClick={onClose}
              className="p-1.5 sm:p-2 rounded-lg text-zinc-400 hover:text-zinc-100 hover:bg-surface-raised transition-colors">
              <IconX size={18} />
            </button>
            <div className="flex-1 min-w-0">
              <h2 className="text-sm sm:text-base font-semibold text-zinc-100 truncate">{title}</h2>
              {subtitle && <p className="text-xs sm:text-sm text-zinc-400 truncate">{subtitle}</p>}
            </div>
            {headerActions && <div className="flex items-center gap-2 shrink-0">{headerActions}</div>}
            {/* Desktop: footer buttons in header bar */}
            {footer && !headerActions && (
              <div className="hidden sm:flex items-center gap-2 shrink-0">{footer}</div>
            )}
          </div>
          {/* Body */}
          <div className="flex-1 overflow-y-auto">{children}</div>
          {/* Mobile: footer pinned at bottom */}
          {footer && !headerActions && (
            <div className="sm:hidden px-4 py-3 border-t border-border-default shrink-0 bg-surface-overlay">
              {footer}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-[200] flex flex-col bg-surface-base animate-slide-up">
      {/* Header */}
      <div className="flex items-center gap-4 px-6 py-4 border-b border-border-default shrink-0 bg-surface-overlay">
        <button onClick={onClose}
          className="p-2 rounded-lg text-zinc-400 hover:text-zinc-100 hover:bg-surface-raised transition-colors">
          <IconX size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold text-zinc-100 truncate">{title}</h2>
          {subtitle && <p className="text-sm text-zinc-400 truncate">{subtitle}</p>}
        </div>
        {headerActions && (
          <div className="flex items-center gap-2 shrink-0">{headerActions}</div>
        )}
        {footer && !headerActions && (
          <div className="flex items-center gap-2 shrink-0">{footer}</div>
        )}
      </div>
      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {children}
      </div>
    </div>
  )
}
